import React, { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { REVIEWS } from "../data/mock";
import { useLang } from "../context/LanguageContext";

const CARDS_PER_VIEW = { mobile: 1, tablet: 2, desktop: 3 };

const ReviewsSection = () => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 640) setPerView(CARDS_PER_VIEW.mobile);
      else if (window.innerWidth < 1024) setPerView(CARDS_PER_VIEW.tablet);
      else setPerView(CARDS_PER_VIEW.desktop);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIndex = Math.max(0, REVIEWS.length - perView);
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxIndex]);

  return (
    <section className="bg-slate-50 dark:bg-zinc-950 py-16 md:py-20 transition-colors">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">{t("reviews.title")}</h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={22} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="font-bold text-slate-900 dark:text-white text-lg ml-1">5.0</span>
            <span className="text-slate-500 dark:text-slate-400 text-sm">· {t("reviews.count")}</span>
          </div>
        </div>

        <div className="relative">
          <button onClick={prev} aria-label="Previous" className="absolute left-0 top-1/2 -translate-y-1/2 z-10 -ml-2 sm:-ml-4 w-10 h-10 rounded-full bg-white dark:bg-zinc-900 shadow-md hover:shadow-lg flex items-center justify-center text-slate-700 dark:text-slate-200 hover:text-[#800000] transition-all">
            <ChevronLeft size={22} />
          </button>

          <div className="overflow-hidden">
            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${(index * 100) / perView}%)` }}>
              {REVIEWS.map((r) => (
                <div key={r.id} className="flex-shrink-0 px-3" style={{ width: `${100 / perView}%` }}>
                  <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 h-full border border-slate-100 dark:border-zinc-800">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} size={16} className="text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{r.time}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-4 h-4">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
                        </svg>
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">{t("reviews.google")}</span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-200 text-sm leading-relaxed mb-4 min-h-[60px]">{r.text}</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-slate-100 dark:border-zinc-800">
                      <div className="w-9 h-9 rounded-full bg-[#800000] text-white flex items-center justify-center font-semibold text-sm">{r.name.charAt(0)}</div>
                      <span className="font-semibold text-slate-900 dark:text-white text-sm">{r.name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={next} aria-label="Next" className="absolute right-0 top-1/2 -translate-y-1/2 z-10 -mr-2 sm:-mr-4 w-10 h-10 rounded-full bg-white dark:bg-zinc-900 shadow-md hover:shadow-lg flex items-center justify-center text-slate-700 dark:text-slate-200 hover:text-[#800000] transition-all">
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-[#800000]" : "w-2 bg-slate-300 dark:bg-zinc-700"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
